/* Validate the linked embroidery fee before normal checkout navigation. */
(() => {
  function validate(cart, expected) {
    if (!cart || !Array.isArray(cart.items)) return false;
    const garments = cart.items.filter(i => i.properties?._pointelle_role === 'garment');
    const icons = cart.items.filter(i => i.properties?._pointelle_role === 'icon');
    if (!garments.length && !icons.length) return true;
    if (!expected || !expected.feeVariantId || !Number.isInteger(expected.feeCents) || expected.feeCents <= 0) return false;
    const pairs = new Set();
    for (const parent of garments) {
      const pair = parent.properties._pointelle_pair;
      if (!pair || pairs.has(pair) || !Number.isInteger(parent.quantity) || parent.quantity < 1) return false;
      pairs.add(pair);
      const children = icons.filter(i => i.properties._pointelle_pair === pair);
      if (children.length !== 1) return false;
      const child = children[0];
      if (child.parent_relationship?.parent_key !== parent.key || child.quantity !== parent.quantity ||
          String(child.variant_id) !== String(expected.feeVariantId) || child.original_price !== expected.feeCents) return false;
    }
    return icons.length === garments.length && icons.every(i => pairs.has(i.properties._pointelle_pair));
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = validate; return; }
  const marker = '[data-pointelle-pair], .pi-cart-garment-properties';
  const checkoutSelector = '[name="checkout"], .cart__checkout-button, a[href^="/checkout"], a[href*="/checkout?"]';
  let pending = false, resuming = false;
  // The gift check runs first and, on a Pointelle cart, calls PointelleCheckout.finish
  // instead of clicking Checkout again. finish() checks the garment and fee once.
  // A later click is ignored while this approval lasts, so the two checks cannot
  // restart each other. Any cart change resets it.
  const APPROVAL_MS = 60000;
  let approvedUntil = 0;
  const isApproved = () => Date.now() < approvedUntil;
  const resetApproval = () => { approvedUntil = 0; };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, resetApproval);
  window.addEventListener('pageshow', event => { if (event.persisted) resetApproval(); });
  const hasPointelle = () => !!document.querySelector(marker);
  const sync = () => document.documentElement.classList.toggle('pi-has-custom-cart', hasPointelle());
  const style = document.createElement('style');
  style.textContent = '.pi-has-custom-cart .cart__dynamic-checkout-buttons,.pi-has-custom-cart .additional-checkout-buttons,.pi-has-custom-cart .cart__shop-pay-button{display:none!important}.pi-checkout-error{color:#9e1743;font-size:14px;padding:12px 0}';
  document.head.append(style);
  new MutationObserver(sync).observe(document.body, { childList: true, subtree: true });
  sync();
  function message(control, text) {
    const host = control.closest('cart-drawer, #main-cart-footer, cart-items') || document.querySelector('#main-cart-footer') || control.parentElement;
    let error = host.querySelector('.pi-checkout-error');
    if (!error) { error = document.createElement('div'); error.className = 'pi-checkout-error'; error.setAttribute('role', 'alert'); error.tabIndex = -1; host.append(error); }
    error.textContent = text; error.focus();
  }
  function liveControl(control) {
    if (control instanceof Element && control.isConnected) return control;
    return document.querySelector('#CartDrawer-Checkout, #checkout.cart__checkout-button, .cart__checkout-button, [name="checkout"]') || control;
  }
  async function check(control, resume, onFail) {
    if (pending) return;
    pending = true;
    const button = liveControl(control);
    button?.setAttribute?.('aria-busy', 'true');
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw Error('cart unavailable');
      const cart = await response.json();
      let expected;
      if (cart.items.some(item => item.properties?._pointelle_role)) {
        const settings = JSON.parse(document.getElementById('pointelle-global-settings').textContent);
        const handle = settings.feeProductUrl?.split('/products/')[1]?.split('?')[0];
        if (!handle) throw Error('missing fee product');
        const feeResponse = await fetch((window.Shopify?.routes?.root || '/') + 'products/' + handle + '.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!feeResponse.ok) throw Error('fee unavailable');
        const fee = (await feeResponse.json()).variants.find(variant => String(variant.id) === String(settings.feeVariantId));
        if (!fee?.available) throw Error('fee unavailable');
        expected = { feeVariantId: fee.id, feeCents: fee.price };
      }
      if (!validate(cart, expected)) throw Error('incomplete embroidery pair');
      approvedUntil = Date.now() + APPROVAL_MS;
      resuming = true;
      try { resume(); } finally { resuming = false; }
    } catch (error) {
      message(liveControl(button), 'Your embroidery and garment need to match before checkout. Please review your cart or remove the custom garment and add it again. No order has been placed.');
      onFail?.();
    } finally { pending = false; button?.removeAttribute?.('aria-busy'); }
  }
  window.PointelleCheckout = {
    hasCart: hasPointelle,
    finish(control, hooks) {
      check(liveControl(control), () => hooks.proceed(), () => hooks.release());
    },
  };
  // Bubble phase, so the gift check's capture listener runs first on a Pointelle cart
  // and calls finish() once. These listeners cover checkout only when that gift check
  // does not take the event.
  window.addEventListener('click', event => {
    if (resuming || isApproved() || (!pending && !hasPointelle())) return;
    const control = event.target.closest?.(checkoutSelector);
    if (!control) return;
    event.preventDefault(); event.stopImmediatePropagation();
    check(control, () => control.click());
  });
  window.addEventListener('submit', event => {
    if (resuming || isApproved() || (!pending && !hasPointelle())) return;
    const form = event.target, submitter = event.submitter;
    if (!submitter?.matches(checkoutSelector) && !/\/checkout(?:[/?]|$)/.test(form.action || '')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    check(submitter || form, () => form.requestSubmit(submitter || undefined));
  });
})();
